import { ArrowRightIcon } from 'lucide-react'
import React from 'react'

import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { useGetVisionPlans } from 'components/hooks/member_onboarding_v2/useGetVisionPlans'
import LoadingSpinnerCentered from 'components/reusable/LoadingSpinnerCentered'
import { Banner, Button, ButtonGroup, useBreakpoint } from 'slab'
import { type GrapeMoney } from 'types'
import { usePostMemberOnboardingEnrollmentEventsEnrollmentEventIdEnrollmentsEnroll as useCreateEnrollment } from 'utils/web_api'

import { OnboardingGenericErrorMessage } from '../components/OnboardingGenericErrorMessage'
import { PageErrorBanner } from '../components/PageErrorBanner'
import { PartialCoverageWarningBanner } from '../components/PartialCoverageWarningBanner/PartialCoverageWarningBanner'
import VisionPlanCard from '../components/VisionPlanCard'
import { useOnboarding } from '../OnboardingContext'
import {
  type DentalPlan,
  type StepComponentProps,
  type VisionPlan,
} from '../types'

const BSCA_PROVIDER_DIRECTORY_URL = 'https://www.blueshieldca.com/fad/home'
const VSP_PROVIDER_DIRECTORY_URL = 'https://www.vsp.com/eye-doctor'

export function VisionPlansPage ({
  navigateToNextStep,
  navigateToPreviousStep,
}: StepComponentProps) {
  const { onboardingData, deleteEnrollment, planChangeSummary } =
    useOnboarding()
  const { enrollmentEventId } = useOnboarding()
  const [submitError, setSubmitError] =
    React.useState<GrapeAPIErrorType | null>(null)
  const {
    data: visionPlanData,
    isLoading,
    error,
  } = useGetVisionPlans({ enrollmentEventId })
  const { breakpointIsAtLeast } = useBreakpoint()
  const availablePlans = visionPlanData?.data ?? []
  const currentPlan =
    onboardingData?.enrollments?.find((e) => e.covers_vision) || null
  const renewalPlanFromPlanSummary =
    planChangeSummary?.next_plan_year.vision_plan || null

  let renewalPlan = null
  if (renewalPlanFromPlanSummary) {
    renewalPlan = availablePlans.find(
      (plan) => plan.plan_code === renewalPlanFromPlanSummary.plan_code
    )
    if (renewalPlan) {
      renewalPlan.premium =
        renewalPlanFromPlanSummary.premium_amount as GrapeMoney
      renewalPlan.features =
        renewalPlanFromPlanSummary.features as VisionPlan['features']
    }
  }

  const createEnrollment = useCreateEnrollment({
    mutation: {
      onSuccess: () => {
        navigateToNextStep?.()
      },
      onError: (error) => {
        setSubmitError(error as GrapeAPIErrorType)
      },
    },
  })

  const deleteMutation = deleteEnrollment(currentPlan?.id || '')

  const vspAvailable = availablePlans.some(
    (plan) => plan.carrier.toLowerCase() === 'vsp'
  )
  const bscaAvailable = availablePlans.some(
    (plan) => plan.carrier.toLowerCase() === 'blueshield of california'
  )

  const getVisionPlansDescription = () => {
    if (availablePlans.length === 0) {
      return 'Your employer is offering you coverage for vision plans, but unfortunately no plans are available in you area.'
    }

    let providerNames = ''
    let providerLinkList = null

    const availableProviders = []
    const providerLinks = []
    if (vspAvailable) {
      availableProviders.push('VSP')
      providerLinks.push(
        <a key='guardian' href={VSP_PROVIDER_DIRECTORY_URL}>
          VSP's provider directory
        </a>
      )
    }
    if (bscaAvailable) {
      availableProviders.push('BlueShield of California')
      providerLinks.push(
        <a key='bsca' href={BSCA_PROVIDER_DIRECTORY_URL}>
          BlueShield of California's provider directory
        </a>
      )
    }

    // Format provider names
    if (availableProviders.length === 1) {
      providerNames = availableProviders[0]
    } else if (availableProviders.length === 2) {
      providerNames = `${availableProviders[0]} and ${availableProviders[1]}`
    } else if (availableProviders.length > 2) {
      const lastProvider = availableProviders.pop()
      providerNames = `${availableProviders.join(', ')}, and ${lastProvider}`
    }

    // Format provider links
    if (providerLinks.length === 1) {
      providerLinkList = providerLinks[0]
    } else if (providerLinks.length === 2) {
      providerLinkList = (
        <>
          {providerLinks[0]}
          {' or '}
          {providerLinks[1]}
        </>
      )
    } else if (providerLinks.length > 2) {
      providerLinkList = (
        <>
          {providerLinks.map((link, index) => (
            <React.Fragment key={index}>
              {link}
              {index < providerLinks.length - 2 && ', '}
              {index === providerLinks.length - 2 && ', or '}
            </React.Fragment>
          ))}
        </>
      )
    }

    return (
      <>
        <p>
          Vision plans in your area are available through {providerNames}. We'll
          enroll you and any dependents using the same information you provide
          for your medical plans.
        </p>
        <br />
        <p>You can search for in-network eye doctors at {providerLinkList}.</p>
      </>
    )
  }

  const onSelectPlan = (plan: VisionPlan | DentalPlan) => {
    createEnrollment.mutate({
      enrollmentEventId,
      data: {
        plan_code: plan.plan_code,
        carrier_name: plan.carrier,
        state: 'in_member_cart',
        covers_medical: false,
        covers_dental: false,
        covers_vision: true,
        quoted_premium: Number(plan.premium.subunits / 100),
      },
    })
  }

  const onRemovePlan = () => {
    deleteMutation.mutate()
    navigateToNextStep?.()
  }

  if (isLoading) {
    return <LoadingSpinnerCentered />
  }

  if (error) {
    return <PageErrorBanner error={error} />
  }

  if (submitError) {
    return (
      <Banner
        title='A problem has occurred while processing your request'
        type='critical'
      >
        <OnboardingGenericErrorMessage error={submitError} />
      </Banner>
    )
  }

  function renderButtons () {
    const isMobile = !breakpointIsAtLeast('xs')

    const backButton = (
      <Button
        size='large'
        variant='secondary'
        onClick={() => navigateToPreviousStep?.(onboardingData)}
      >
        Back
      </Button>
    )

    const removeButton = (
      <Button
        data-testid='remove-plan-button'
        size='large'
        variant='tertiary-deemphasized'
        onClick={() => onRemovePlan()}
      >
        I don't want a vision plan
      </Button>
    )

    const continueButton = (
      <Button
        size='large'
        variant='primary'
        rightIcon={ArrowRightIcon}
        onClick={() => navigateToNextStep()}
      >
        Continue
      </Button>
    )

    const skipButton = (
      <Button
        variant='tertiary-deemphasized'
        onClick={() => navigateToNextStep()}
        size='large'
      >
        Skip vision
      </Button>
    )

    if (currentPlan) {
      return isMobile
        ? (
          <ButtonGroup layout='vertical' size='large'>
            {continueButton}
            {backButton}
            {removeButton}
          </ButtonGroup>
          )
        : (
          <div className='flex items-center justify-between'>
            {backButton}
            <ButtonGroup
              size='large'
              layout={isMobile ? 'vertical' : 'horizontal'}
            >
              {removeButton}
              {continueButton}
            </ButtonGroup>
          </div>
          )
    }

    return isMobile
      ? (
        <ButtonGroup size='large' layout='vertical'>
          {backButton}
          {skipButton}
        </ButtonGroup>
        )
      : (
        <div className='flex items-center justify-between gap-2'>
          {backButton}
          {skipButton}
        </div>
        )
  }

  return (
    <div data-testid='VisionPlansPage' className='max-w-[44rem]'>
      <div className='mb-10'>
        <h1 className='heading-lg text-gray-800 mb-3'>
          Choose your vision insurance
        </h1>
        <span className='body text-gray-500'>
          {getVisionPlansDescription()}
        </span>
      </div>
      {availablePlans.length > 0 && (
        <div className='flex flex-col mb-10 gap-y-6'>
          {renewalPlan && (
            <div>
              <VisionPlanCard
                badgeCategory='renewal'
                key={`${renewalPlan.carrier}-${renewalPlan.name}`}
                displayedPlan={renewalPlan}
                onSelectPlan={onSelectPlan}
                currentPlan={currentPlan}
              />
              {planChangeSummary && (
                <PartialCoverageWarningBanner
                  planChangeSummary={planChangeSummary}
                  coverageType='vision'
                  renderedInPlanPage
                />
              )}
            </div>
          )}
          {availablePlans.map((plan) => {
            if (plan.plan_code === renewalPlanFromPlanSummary?.plan_code) {
              return null
            }
            return (
              <VisionPlanCard
                key={`${plan.carrier}-${plan.name}`}
                displayedPlan={plan}
                onSelectPlan={onSelectPlan}
                currentPlan={currentPlan}
              />
            )
          })}
        </div>
      )}
      {renderButtons()}
    </div>
  )
}
